/* eslint-disable jsdoc/require-param-type, jsdoc/require-returns-type */
import { get } from 'svelte/store';
import { app, setGlobalStore } from '$lib/stores/app/store';
import { user } from '$lib/stores/user/store';
import { ApiService } from '$lib/services/api';
import { getDeviceId } from '$lib/utils/device-id';
import * as Sentry from '@sentry/sveltekit';
import {
	AB_API_HOSTS,
	DEVICE_KEY_MAP,
	PROFILE,
	DATA_TYPES,
	DATA_KEYS,
} from '$lib/services/experiments/client_v2/constants';
import { page } from '$app/stores';
import { isbot } from 'isbot';
import {
	has1_0Account,
	has2_0Account,
	is1_0OriginatingUser,
	is2_0OriginatingUser,
} from '$lib/utils/aofl-accounts';
import type { App } from '$lib/stores/app/types';

/** User payload for experiment API */
interface UserData {
	device_id: string;
	master_id: string;
	session_id: string;
	user_id: string;
}

/** Single device/data item in API payload */
interface DataItem {
	type: string;
	id: string;
	key: string;
	value: string;
}

/** Page context at divergence */
export interface ExperimentContext {
	pageName?: string;
	pageUrl?: string;
}

/** Page-level context nested inside event_metadata (client context fields + page fields) */
interface PageMetadata {
	// client context (from ApiService.getClientContext)
	app_version?: string;
	is_web?: boolean;
	is_automated_testing?: boolean;
	preserve_session_device_id?: boolean;
	client_url?: string | null;
	viewer_country?: string;
	viewer_country_region?: string;
	client_platform?: string;
	// page fields
	page_name: string;
	page_url: string;
	navigator?: string;
	window_url?: string;
	svelte_page_url?: unknown;
	is_tablet?: string | null;
	is_desktop?: string | null;
}

/** event_metadata shape for Get/MarkSeen (proto field 6, google.protobuf.Any) */
interface EventMetadata {
	page_metadata: PageMetadata;
	subscription_metadata: { user_subscription_type: string | null };
}

/** Payload for Get experiments API */
interface GetExperimentsPayload {
	user: UserData;
	experiment_keys: string[];
	profile: string;
	data: DataItem[];
	auto_mark_seen: boolean;
	event_metadata?: EventMetadata;
}

/** Experiment shape from API / store (compatible with App.Experiment) */
interface ExperimentResult {
	key: string;
	variant: string;
	experiment_id: string | null;
	seen: boolean;
	assigned: boolean;
}

/** Response shape from experiment Get API. */
interface GetExperimentsResponse {
	results?: ExperimentResult[];
}

/**
 * Experiment client v2: single Get at divergence with auto_mark_seen.
 */
class ExperimentClientV2 {
	/**
	 * Returns the assigned variant for an experiment at a divergence point and records exposure (Get with auto_mark_seen).
	 * @param experimentKey - Experiment key (e.g. from ACTIVE_EXPERIMENTS).
	 * @param autoMarkSeen - Whether to mark the experiment as seen.
	 * @returns The assigned variant string, or null if not assigned or API failed.
	 */
	async initializeExperiment(experimentKey: string, autoMarkSeen = true): Promise<string | null> {
		if (!experimentKey) return null;

		const isBotSession = await this.#handleBotExperimentAssignment(experimentKey);
		if (isBotSession) return null;

		const subscriptionData = this.#getSubscriptionData();
		const data = [...this.#getDeviceData()];
		if (subscriptionData) data.push(...subscriptionData);
		const event_metadata: EventMetadata = {
			page_metadata: this.#buildPageMetadata(),
			subscription_metadata: this.#getSubscriptionMetadata(),
		};
		const payload: GetExperimentsPayload = {
			user: this.#getUserData(),
			experiment_keys: [experimentKey],
			profile: PROFILE,
			data,
			auto_mark_seen: autoMarkSeen,
			event_metadata,
		};
		const results = await this.#getExperiments(payload);
		const experiment = results?.find((exp: ExperimentResult) => exp.key === experimentKey);
		return experiment?.variant ?? null;
	}

	/**
	 * Handles bot sessions: writes a default control experiment to store if empty, or tracks debug metric; returns true so caller skips API.
	 * How it works: Uses isbot(userAgent); if bot and no experiments in store, sets one default experiment for the key and returns true; otherwise tracks metric and returns true. Non-bot returns false.
	 * @param experimentKey - Experiment key to create a default assignment for when store is empty.
	 * @returns true if this was a bot session (caller should skip Get), false otherwise.
	 */
	async #handleBotExperimentAssignment(experimentKey: string): Promise<boolean> {
		const userAgent = typeof navigator !== 'undefined' ? (navigator?.userAgent ?? '') : '';
		if (isbot(userAgent)) {
			const existingExperiments = get(app).global.experiments;
			if (!existingExperiments || existingExperiments.length === 0) {
				const defaultExperiment: App.Experiment = {
					key: experimentKey,
					variant: 'control_variant',
					seen: false,
					assigned: false,
					experiment_id: null,
				};
				await setGlobalStore({ experiments: [defaultExperiment] });
			} else {
				Sentry.captureMessage('ExperimentClientV2: bot session detected', {
					level: 'warning',
					extra: { user_agent: userAgent },
				});
			}
			return true;
		}
		return false;
	}

	/**
	 * @param experimentKey - Experiment key to look up.
	 * @param variant - Variant string to compare (e.g. 'test', 'default').
	 * @returns true if the stored variant matches, false otherwise or if missing.
	 */
	isVariantActive(experimentKey: string, variant: string): boolean {
		const {
			global: { experiments },
		} = get(app);
		if (!experiments?.length || !experimentKey) return false;
		const experiment = experiments.find((exp) => exp.key === experimentKey);
		return experiment?.variant === variant;
	}

	/**
	 * @param experimentKey - Experiment key to look up.
	 * @param variants - List of variant strings (e.g. ['test', 'test-a']).
	 * @returns true if stored variant is in the list, false otherwise or if missing.
	 */
	isAnyVariantActive(experimentKey: string, variants: string[]): boolean {
		const {
			global: { experiments },
		} = get(app);
		if (!experiments?.length || !experimentKey || !Array.isArray(variants)) return false;
		const experiment = experiments.find((exp) => exp.key === experimentKey);
		return experiment?.variant != null && variants.includes(experiment.variant);
	}

	/**
	 * @param experimentKeys - List of experiment keys to check.
	 * @returns true if any key is assigned, false if none or store empty.
	 */
	isAnyAssigned(experimentKeys: string[]): boolean {
		const {
			global: { experiments },
		} = get(app);
		if (!Array.isArray(experimentKeys) || !experimentKeys.length || !experiments?.length)
			return false;
		for (const key of experimentKeys) {
			const experiment = experiments.find((exp) => exp.key === key);
			if (experiment?.assigned || typeof experiment?.experiment_id === 'string') return true;
		}
		return false;
	}

	/**
	 * Returns the experiment/analytics API host for the current environment (development, staging, production).
	 * @returns API host URL for the current environment.
	 */
	get #AB_API_HOST(): string {
		const {
			global: { environment },
		} = get(app);
		return AB_API_HOSTS[environment as keyof typeof AB_API_HOSTS];
	}

	/**
	 * Builds the user payload for experiment API calls (device_id, master_id, session_id, user_id).
	 * @returns User payload for the experiment API.
	 */
	#getUserData(): UserData {
		const userStore = get(user);
		return {
			device_id: getDeviceId() ?? '',
			master_id: userStore.userToProductUuid || 'null',
			session_id: 'null',
			user_id: userStore.userToProductUuid || 'null',
		};
	}

	/**
	 * Builds the device/data array for experiment API calls from app meta and global state.
	 * @returns Device/data items for the experiment API.
	 */
	#getDeviceData(): DataItem[] {
		const appMeta = get(app).meta;
		const appGlobal = get(app).global as Record<string, unknown>;
		const deviceId = getDeviceId() ?? '';
		const appData = Object.entries(DEVICE_KEY_MAP).flatMap(([key, metaKey]) => {
			if (appMeta[metaKey as keyof typeof appMeta] === undefined) return [];
			return [
				{
					type: DATA_TYPES.DEVICE,
					id: deviceId,
					key,
					value: String(appMeta[metaKey as keyof typeof appMeta]),
				},
			];
		});
		const globalData = Object.entries(DEVICE_KEY_MAP).flatMap(([key, globalKey]) => {
			if (appGlobal[globalKey] === undefined) return [];
			return [
				{
					type: DATA_TYPES.DEVICE,
					id: deviceId,
					key,
					value: String(appGlobal[globalKey]),
				},
			];
		});
		return [...appData, ...globalData];
	}

	/**
	 * Builds the subscription metadata object sent as subscriptionMetadata in MarkSeen payloads.
	 * @returns Object with user_subscription_type, or { user_subscription_type: null } when unavailable.
	 */
	#getSubscriptionMetadata(): { user_subscription_type: string | null } {
		const subscriptionData = this.#getSubscriptionData();
		const userSubscription =
			subscriptionData?.find((item) => item.key === DATA_KEYS.USER_SUBSCRIPTION_TYPE)?.value ??
			null;
		return { user_subscription_type: userSubscription };
	}

	/**
	 * Builds subscription-related data items for the experiment API when the user has a userToProductUuid.
	 * @returns Subscription data items, or null when user has no userToProductUuid.
	 */
	#getSubscriptionData(): DataItem[] | null {
		const userStore = get(user);
		if (!userStore.userToProductUuid) return null;
		const subscriptionStatus: DataItem = {
			type: DATA_TYPES.MASTER,
			id: userStore.userToProductUuid ?? '',
			key: DATA_KEYS.SUBSCRIPTION_STATUS,
			value: userStore.accountStatus ?? '',
		};
		let value: string = '';
		switch (true) {
			case has1_0Account() && !has2_0Account():
				value = '1.0_only';
				break;
			case is1_0OriginatingUser() && has2_0Account():
				value = '1.0_originating_mixed';
				break;
			case has2_0Account() && !has1_0Account():
				value = '2.0_only';
				break;
			case has2_0Account() && is2_0OriginatingUser():
				value = '2.0_originating_mixed';
				break;
			default:
				value = '';
				break;
		}
		const userSubscriptionType: DataItem = {
			type: DATA_TYPES.MASTER,
			id: userStore.userToProductUuid ?? '',
			key: DATA_KEYS.USER_SUBSCRIPTION_TYPE,
			value,
		};
		return [subscriptionStatus, userSubscriptionType];
	}

	/**
	 * Builds the page_metadata object nested inside event_metadata for Get/MarkSeen API calls.
	 * @returns Page metadata object for the API.
	 */
	#buildPageMetadata(): PageMetadata {
		const meta = get(app).meta;
		return {
			...ApiService.getClientContext(),
			page_name: get(page)?.url?.pathname ?? '',
			page_url: get(page)?.url?.href ?? get(page)?.url?.pathname ?? '',
			navigator: window.navigator?.userAgent ?? undefined,
			is_tablet: meta?.isTabletViewer ?? null,
			is_desktop: meta?.isDesktopViewer ?? null,
		};
	}

	/**
	 * Calls the experiment Get API, merges results into app.global.experiments, and returns the merged list (or existing on error).
	 * @param payload - Get payload (user, experiment_keys, profile, data, auto_mark_seen, optional event_metadata).
	 * @returns Merged experiment array on success, or existing experiments on failure.
	 */
	async #getExperiments(payload: GetExperimentsPayload): Promise<Array<ExperimentResult>> {
		let existingExperiments: Array<ExperimentResult> = [];
		try {
			const {
				global: { experiments: oldExperiments },
			} = get(app);
			existingExperiments = oldExperiments;

			const response = (await ApiService.getExperiments(
				this.#AB_API_HOST,
				payload
			)) as GetExperimentsResponse;
			const newExperiments = response?.results;
			if (!newExperiments) {
				await setGlobalStore({ experiments: [] });
				return [];
			}

			const autoMarkSeen = payload.auto_mark_seen === true;
			const experimentsForStore: App.Experiment[] = newExperiments.map((exp) => ({
				key: exp.key,
				variant: exp.variant,
				experiment_id: exp.experiment_id ?? null,
				seen: autoMarkSeen,
				assigned: exp.assigned ?? false,
			}));
			const experimentsMap = new Map<string, App.Experiment>();
			if (existingExperiments) {
				for (const exp of existingExperiments) experimentsMap.set(exp.key, { ...exp });
			}
			for (const exp of experimentsForStore) experimentsMap.set(exp.key, exp);
			await setGlobalStore({ experiments: [...experimentsMap.values()] });
			return [...experimentsMap.values()];
		} catch (error) {
			Sentry.captureException(error, { extra: { action: 'Error: Failed To Get Experiments' } });
			return existingExperiments;
		}
	}

	/**
	 * Marks an experiment as seen.
	 * @param experimentKey - The key of the experiment to mark as seen.
	 * @param eventMetadata - The event metadata to be sent to the API.
	 */
	async #markExperimentAsSeen(experimentKey: string, eventMetadata: EventMetadata): Promise<void> {
		const {
			global: { experiments },
		} = get(app);

		const experiment = experiments.find((exp) => exp.key === experimentKey);
		if (!experiment) return;

		if (experiment.experiment_id && !experiment.seen) {
			const payload = {
				...experiment,
				profile: PROFILE,
				user: this.#getUserData(),
				pageMetadata: eventMetadata.page_metadata,
				subscriptionMetadata: eventMetadata.subscription_metadata,
			};

			try {
				await ApiService.markExperimentSeen(
					this.#AB_API_HOST,
					payload as unknown as Parameters<typeof ApiService.markExperimentSeen>[1]
				);
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: Failed To Mark Experiment Seen',
						payload,
					},
				});
			}

			experiment.seen = true;
			await setGlobalStore({ experiments });
		}
	}
}

export const experimentClientV2 = new ExperimentClientV2();
